import { useTranslation } from 'react-i18next';

import { Breadcrumbs, NavItem, Section } from '../../../Common/Layout';
import { useBrandColor } from '../../../../common/Common/ThemeProvider';
import { useNavigation } from '../../../Common/useNavigation';
import { useParticipant } from '../../ParticipantProvider';
import GetRegistrationSummaryForDashboardQuery from '../GetRegistrationSummaryForDashboardQuery';
import UI from '../../../../common/UI';
import useDocumentTitle from '../../../../common/useDocumentTitle';
import useLocale from '../../../../common/useLocale';
import useProject from '../../../../common/useProject';
import useQuery from '../../../../api/useQuery';

export interface OrdersPageProps {
  participantId: string;
  eventId: string;
  token: string;
}

const OrdersPage = ({ participantId, eventId, token }: OrdersPageProps) => {
  const { t } = useTranslation();
  const { r } = useNavigation();
  const { formatCurrency, formatDate, parseDate } = useLocale();
  const { impersonating } = useParticipant();
  const project = useProject();

  const { data } = useQuery(
    GetRegistrationSummaryForDashboardQuery,
    {
      variables: {
        participant: participantId,
        event: eventId,
        token,
        registrations_limit: 0,
        waitlist_registrations_limit: 0,
        teams_limit: 0,
        include_filtered_teams: false,
      },
    },
  );

  const summary = data?.registrationSummary;

  useDocumentTitle([
    summary?.participant.full_name,
    summary?.event.title,
    'Atleta',
  ]);

  const themeInitialized = useBrandColor(summary?.event.brand_color);

  if (!data || !themeInitialized) {
    return <UI.PageLoader />;
  }

  return (
    <>
      <Breadcrumbs hideHome={impersonating}>
        <UI.Link to={r('Registrations', {
          eventId,
          projectId: project.id,
          participantId,
          token,
        })}
        >
          {summary.event.title}
        </UI.Link>
        <UI.Strong>{t('orders')}</UI.Strong>
      </Breadcrumbs>
      <Section title={t('orders')}>
        {summary.orders.map((order) => (
          <NavItem
            to={r('Order', {
              participantId,
              projectId: project.id,
              eventId,
              token,
              orderId: order.id,
            })}
            title={`${t('order')} ${order.invoice_id}`}
            key={order.id}
          >
            <UI.Div>
              <UI.H4>
                {t('order')}
                {' '}
                {order.invoice_id}
              </UI.H4>
              <UI.Div>
                <UI.Delimit>
                  {formatCurrency(order.invoice_amount)}
                  {formatDate(
                    parseDate(order.invoice_date, { timezone: 'UTC', format: 'internal_date' }),
                    { format: 'display_compact_date' },
                  )}
                </UI.Delimit>
              </UI.Div>
              {order.remaining_amount > 0 && (
                <UI.Warning sc={{ mt: 2 }}>
                  <UI.Strong>
                    {t('remaining')}
                    {': '}
                    {formatCurrency(order.remaining_amount)}
                  </UI.Strong>
                  {order.due_date && (
                    <>
                      {' ('}
                      {formatDate(
                        parseDate(order.due_date, {
                          timezone: 'UTC',
                          format: 'internal_date',
                        }),
                        { format: 'display_date' },
                      )}
                      )
                    </>
                  )}
                </UI.Warning>
              )}
            </UI.Div>
          </NavItem>
        ))}
        {summary.orders.length === 0 && (
          <UI.Div sc={{ muted: true }}>
            {t('no_invoices_yet')}
          </UI.Div>
        )}
      </Section>
    </>
  );
};

export default OrdersPage;
